/**
 * Toast 容器组件
 * 用于显示全局 toast 消息
 */
import React from 'react';
import { useAtom } from 'jotai';
import { motion, AnimatePresence } from 'framer-motion';
import { toastMessagesAtom, removeToastAtom } from '../../atoms/v0/uiAtoms';
import { FiX, FiCheckCircle, FiAlertCircle, FiInfo, FiAlertTriangle } from 'react-icons/fi';

export const ToastContainer: React.FC = () => {
  const [toastMessages] = useAtom(toastMessagesAtom);
  const [, removeToast] = useAtom(removeToastAtom);

  // 获取状态对应的图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <FiCheckCircle className="w-5 h-5" />;
      case 'error':
        return <FiAlertCircle className="w-5 h-5" />;
      case 'warning':
        return <FiAlertTriangle className="w-5 h-5" />;
      case 'info':
      default:
        return <FiInfo className="w-5 h-5" />;
    }
  };

  // 获取状态对应的样式
  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'success':
        return {
          container: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700',
          icon: 'text-green-600 dark:text-green-400',
          title: 'text-green-800 dark:text-green-200',
          description: 'text-green-700 dark:text-green-300'
        };
      case 'error':
        return {
          container: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700',
          icon: 'text-red-600 dark:text-red-400',
          title: 'text-red-800 dark:text-red-200',
          description: 'text-red-700 dark:text-red-300'
        };
      case 'warning':
        return {
          container: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700',
          icon: 'text-orange-600 dark:text-orange-400',
          title: 'text-orange-800 dark:text-orange-200',
          description: 'text-orange-700 dark:text-orange-300'
        };
      case 'info':
      default:
        return {
          container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700',
          icon: 'text-blue-600 dark:text-blue-400',
          title: 'text-blue-800 dark:text-blue-200',
          description: 'text-blue-700 dark:text-blue-300'
        };
    }
  };

  if (toastMessages.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
      <AnimatePresence>
        {toastMessages.map((toast) => {
          const styles = getStatusStyles(toast.status);
          
          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, x: 300, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 300, scale: 0.8 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className={`${styles.container} border rounded-lg shadow-lg p-4 backdrop-blur-sm`}
            >
              <div className="flex items-start gap-3">
                {/* 状态图标 */}
                <div className={`flex-shrink-0 ${styles.icon}`}>
                  {getStatusIcon(toast.status)}
                </div>

                {/* 内容 */}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${styles.title}`}>
                    {toast.title}
                  </div>
                  {toast.description && (
                    <div className={`text-sm mt-1 ${styles.description}`}>
                      {toast.description}
                    </div>
                  )}
                </div>

                {/* 关闭按钮 */}
                {toast.isClosable && (
                  <button
                    onClick={() => removeToast(toast.id)}
                    className={`flex-shrink-0 ${styles.icon} hover:opacity-70 transition-opacity`}
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default ToastContainer;
